<!-- eslint-disable @typescript-eslint/no-unused-vars -->
<template>
    <div class="paymentmethodselector" :class="customClass">
        <div class="openbtn" :class="{ opened: isOpen }" @click="toggleDrawer">
            <div class="placeholder" v-if="isPlaceholderVisible">
                <i class="fal fa-credit-card"></i>
                <i class="fas fa-question-circle"></i>
            </div>

            <div class="method" v-if="!isLoading && selectedMethod && !selectedCard && !isOpen">
                <div
                    v-if="selectedMethod.imagePath"
                    class="icon"
                    :data-method-name="methodName(selectedMethod)"
                    :style="{ 'background-image': 'url(' + selectedMethod.imagePath + ')' }"
                ></div>
                <div class="icon" :data-method-name="methodName(selectedMethod)" v-else>
                    <i class="fal fa-credit-card"></i>
                </div>
            </div>

            <div class="card" v-if="!isLoading && selectedCard && !isOpen">
                <div class="brandicon" :class="selectedCard.brand?.toLowerCase()"></div>
                <div class="expiry" v-if="selectedCard.expiryDate"></div>
                <div class="number"><span class="dots">••••</span>{{ selectedCard.info }}</div>
            </div>
            <div v-if="isLoading" class="loader"></div>
        </div>

        <div v-if="isOpen" class="drawerpopover" :class="{ shown: isOpen }">
            <div class="inner">
                <slot name="header"></slot>
                <div class="choose">
                    <div class="title" style="margin-top: 23px; margin-bottom: 12px; padding-bottom: 0px">
                        {{ translations.general.pleaseSelect }}
                    </div>

                    <!-- todo: for now display only offline payment methods -->
                    <template v-if="onlinePaymentMethods.length">
                        <div class="title paymentOnline paymentOnlineTitle">Online</div>
                        <div class="methodslider paymentOnline">
                            <div class="slidable paymentOnline">
                                <div
                                    v-for="onlineMethod in onlinePaymentMethods"
                                    :key="onlineMethod.id"
                                    class="method"
                                    :class="{ selected: isSelected(onlineMethod) }"
                                    @click="selectMethod(onlineMethod, false)"
                                >
                                    <div
                                        v-if="onlineMethod.imagePath"
                                        class="icon"
                                        :data-method-name="methodName(onlineMethod)"
                                        :style="{ 'background-image': 'url(' + onlineMethod.imagePath + ')' }"
                                    ></div>
                                    <div class="icon" :data-method-name="methodName(onlineMethod)" v-else>
                                        <i class="fal fa-credit-card"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-if="offlinePaymentMethods.length">
                        <div v-if="!props.useDiscountsPaymentMethod" class="title paymentOffline paymentOfflineTitle">
                            Offline
                        </div>
                        <div class="methodslider paymentOffline">
                            <div class="slidable paymentOffline">
                                <div
                                    v-for="offlineMethod in offlinePaymentMethods"
                                    :key="offlineMethod.id"
                                    class="method"
                                    :class="{ selected: isSelected(offlineMethod) }"
                                    @click="selectMethod(offlineMethod, false)"
                                >
                                    <div
                                        v-if="offlineMethod.imagePath"
                                        class="icon"
                                        :data-method-name="methodName(offlineMethod)"
                                        :style="{ 'background-image': 'url(' + offlineMethod.imagePath + ')' }"
                                    ></div>
                                    <div class="icon" :data-method-name="methodName(offlineMethod)" v-else>
                                        <i class="fal fa-credit-card"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div
                        class="no-payment-methods"
                        v-if="!offlinePaymentMethods.length && !onlinePaymentMethods.length"
                    >
                        {{ trans_vue.paymentMethodSelector.noPaymentMethodConfigured }}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="payment-method-selector-overlay" :class="{ shown: isOpen }" v-if="isOpen" @click="close"></div>
</template>

<script setup lang="ts">
    import type { ICreditCard } from "@/data/model/CreditCard";
    import type { IPaymentMethod } from "@/data/model/Customer";
    import { Language, PaymentMethodMode, PaymentProvider } from "@/data/model/Enums";
    import { usePaymentStore } from "@/data/stores/payment";
    import { Format } from "@/data/util/Format";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { translations } from "@/shared/localization/translations";
    import { computed, onMounted, ref, watch } from "vue";

    const payment = usePaymentStore();

    const selectedMethod = defineModel<IPaymentMethod | null>("selectedMethod", {
        required: true,
    });

    // define properties
    const props = defineProps<{
        availableMethods?: IPaymentMethod[];
        isLoading: boolean;
        disabled?: boolean;
        customClass?: string;
        useDiscountsPaymentMethod?: boolean;
        hasVouchers?: boolean;
        autoClosePopoverOnSelectPaymentMethod?: boolean;
    }>();

    // define emits
    const emit = defineEmits(["onOpened", "onSelected", "onClosed"]);

    const isOpen = ref(false);
    const isCardFormMode = ref(false);
    const cardFormInitialized = ref(false);
    const isValidatingForm = ref(false);
    const paymentInProgress = ref(false);
    const selectedCard = ref(null as ICreditCard | null); // Adjust the type accordingly
    const walleeHandler = ref<any>(null); // Adjust the type accordingly

    const paymentMethodLanguage = OperatorClient.context.settings?.location?.language ?? Language.German;

    function methodName(method: IPaymentMethod) {
        return method.name ?? Format.translate(method.localizedName, paymentMethodLanguage);
    }

    const onlinePaymentMethods = computed(() => {
        if (!props.availableMethods) return [];
        if (props.useDiscountsPaymentMethod) return [];

        return props.availableMethods.filter((method) => method.mode === PaymentMethodMode.Online);
    });

    const offlinePaymentMethods = computed(() => {
        if (!props.availableMethods) return [];
        if (props.useDiscountsPaymentMethod) {
            return [props.hasVouchers ? payment.vouchersPaymentMethod : payment.discountsPaymentMethod];
        }

        return props.availableMethods.filter((method) => method.mode === PaymentMethodMode.Offline);
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const allAvailablePaymentMethods = computed(() => {
        console.log(onlinePaymentMethods.value.length);
        return [...offlinePaymentMethods.value, ...onlinePaymentMethods.value];
    });

    const isPlaceholderVisible = computed(
        () => !props.isLoading && !selectedMethod.value && !selectedCard.value && !isOpen.value
    );

    function presetPaymentMethod() {
        // preselect first payment method if there is only one
        if (allAvailablePaymentMethods.value.length === 1) {
            selectMethod(allAvailablePaymentMethods.value[0], false);
        }
    }

    function toggleDrawer() {
        if (isOpen.value) {
            close();
        } else if (props.disabled != true) {
            open();
        }
    }

    function open() {
        if (paymentInProgress.value || isValidatingForm.value) return;
        if (props.availableMethods === undefined) return;

        isOpen.value = true;
        emit("onOpened");
    }

    function close() {
        if (paymentInProgress.value || isValidatingForm.value) return;

        isOpen.value = false;
        emit("onClosed");
    }

    function selectMethod(method: IPaymentMethod, closeDrawer: boolean) {
        if (method.type !== "OpenCashDrawer") {
            selectedMethod.value = method;
            selectedCard.value = null;
        }

        emit("onSelected", method);
        if ((props.autoClosePopoverOnSelectPaymentMethod ?? false) || closeDrawer) close();
    }

    function isSelected(method: IPaymentMethod) {
        return selectedMethod.value == method;
    }

    function reset() {
        isOpen.value = false;
        isCardFormMode.value = false;
        cardFormInitialized.value = false;
        isValidatingForm.value = false;
        paymentInProgress.value = false;
        selectedMethod.value = null;
        emit("onSelected", null);
        selectedCard.value = null;
        walleeHandler.value = null;
    }

    onMounted(() => {
        presetPaymentMethod();
    });

    watch(allAvailablePaymentMethods, () => {
        presetPaymentMethod();
    });

    watch(
        () => props.useDiscountsPaymentMethod,
        (useDiscounts) => {
            if (!useDiscounts) {
                reset();
            }
        }
    );

    // define exposed
    defineExpose({
        isOpen,
        open,
        close,
        reset,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .payment-method-selector-overlay {
        position: absolute;
        top: 0;
        left: 0;
        // bottom: 0;
        bottom: 90px; // some random value in the middle of the payment method form
        right: 0;
        opacity: 0;
        background-color: rgba(0, 0, 0, 0.7);
        z-index: 400;
        transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1);

        &.shown {
            display: block;
            opacity: 1;
        }
    }

    .paymentmethodselector {
        > .openbtn {
            position: relative;
            // height: 69px;
            height: 56px;
            width: 85px;
            margin: 0;

            &.opened {
                width: 0;
            }

            > .loader {
                position: absolute;
                top: 9px;
                left: 24px;
                width: 36px;
                height: 36px;
                background-size: 100% 100%;
                background-image: url("/Assets/img/app/loading_green.svg");
            }

            > .method {
                position: absolute;
                cursor: pointer;
                // top: 9px;
                top: 7px;
                left: 11px;
                width: 64px;
                height: 43px;
                box-sizing: border-box;
                border: 1px solid #dedede;
                border-radius: 5px;
                overflow: hidden;

                > .icon {
                    width: 40px;
                    height: 28px;
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center center;
                    margin: 7px auto;

                    > .fa-credit-card {
                        font-size: 28px;
                        display: block;
                        line-height: 28px;
                        text-align: center;
                    }
                }
            }

            > .placeholder {
                position: absolute;
                cursor: pointer;
                // top: 9px;
                top: 7px;
                left: 11px;
                width: 64px;
                height: 43px;
                box-sizing: border-box;
                border: 1px solid #dedede;
                border-radius: 5px;
                overflow: hidden;
                animation: wiggle 2.5s infinite;
                text-align: center;

                > .fa-credit-card {
                    position: absolute;
                    left: 8px;
                    top: 5px;
                    font-size: 28px;
                    color: colors.$fontColor;
                }

                > .fa-question-circle {
                    color: colors.$green;
                    position: absolute;
                    top: 14px;
                    right: 5px;
                    font-size: 23px;
                }

                &:hover {
                    animation: none;
                }

                @keyframes wiggle {
                    0% {
                        transform: rotate(0deg);
                    }

                    70% {
                        transform: rotate(0deg);
                    }

                    75% {
                        transform: rotate(5deg);
                    }

                    80% {
                        transform: rotate(-5deg);
                    }

                    85% {
                        transform: rotate(5deg);
                    }

                    90% {
                        transform: rotate(-5deg);
                    }

                    95% {
                        transform: rotate(5deg);
                    }

                    100% {
                        transform: rotate(0deg);
                    }
                }
            }

            > .card {
                position: absolute;
                cursor: pointer;
                top: 9px;
                left: 11px;
            }
        }

        > .drawerpopover {
            position: absolute;
            bottom: 73px;
            left: 0;
            right: 0;
            background-color: #fff;
            // height: 117px;
            -webkit-border-top-left-radius: 15px;
            -webkit-border-top-right-radius: 15px;
            -moz-border-radius-topleft: 15px;
            -moz-border-radius-topright: 15px;
            border-top-left-radius: 15px;
            border-top-right-radius: 15px;
            -webkit-box-shadow: 0px -5px 7px -5px rgba(72, 72, 72, 0.11);
            -moz-box-shadow: 0px -5px 7px -5px rgba(72, 72, 72, 0.11);
            box-shadow: 0px -5px 7px -5px rgba(72, 72, 72, 0.11);
            transform: translate3d(0, 110px, 0);
            transition:
                transform 0.6s variables.$easeOutExpo,
                height 0.6s variables.$easeOutExpo;
            z-index: 299;

            .no-payment-methods {
                font-size: 16px;
                padding: 1em 1.5em;
            }

            > .inner {
                opacity: 0;
                transition: opacity 0.3s variables.$easeOutExpo;

                > .choose {
                    padding-bottom: 1em;

                    > .title {
                        font-size: 14px;
                        font-weight: 700;
                        padding: 0px 24px 1px 24px;
                    }

                    > .methodslider {
                        position: relative;
                        overflow-x: auto;

                        > .absoluteWidth {
                            position: absolute;
                            width: 100%;
                        }

                        > .slidable {
                            left: 0;
                            top: 0;
                            padding: 8px 24px;
                            white-space: nowrap;

                            > .method {
                                position: relative;
                                width: 90px;
                                height: 36px;
                                border: 2px solid #f5f5f5;
                                border-radius: 20px;
                                display: inline-block;
                                vertical-align: top;
                                margin: 2px 4px 2px 4px;
                                cursor: pointer;
                                transition: border 0.6s variables.$easeOutExpo;

                                &.nameOnly {
                                    width: auto;
                                    line-height: 2.2em;
                                    padding-left: 10px;
                                    padding-right: 10px;
                                    padding-top: 2px;
                                    padding-bottom: 2px;
                                }

                                @include mixins.hover-and-active() {
                                    border-color: #ebebeb;
                                }

                                &.selected {
                                    border-color: colors.$green;
                                }

                                > .icon {
                                    position: relative;
                                    width: 56px;
                                    height: 28px;
                                    background-size: contain;
                                    background-repeat: no-repeat;
                                    background-position: center center;
                                    margin: 2px auto;

                                    > .fa-credit-card {
                                        display: block;
                                        line-height: 28px;
                                        text-align: center;
                                        color: colors.$fontColor;
                                        font-size: 22px;
                                    }

                                    > .visa {
                                        position: absolute;
                                        left: -5px;
                                        top: 9px;
                                        background-image: url("/assets/img/brands/cc_visa.svg");
                                        background-size: contain;
                                        background-repeat: no-repeat;
                                        background-position: center center;
                                        width: 35px;
                                        height: 11px;
                                    }

                                    > .mastercard {
                                        position: absolute;
                                        left: 29px;
                                        top: 6px;
                                        background-image: url("/assets/img/brands/cc_mastercard.svg");
                                        background-size: contain;
                                        background-repeat: no-repeat;
                                        background-position: center center;
                                        width: 29px;
                                        height: 18px;
                                    }

                                    > .plus {
                                        position: absolute;
                                        bottom: 11px;
                                        right: -10px;
                                        width: 16px;
                                        height: 16px;
                                        background-color: #8fb52c;
                                        text-align: center;
                                        border-radius: 8px;

                                        > .fas {
                                            display: inline-block;
                                            vertical-align: top;
                                            color: #ffffff;
                                            font-size: 10px;
                                            line-height: 16px;
                                        }
                                    }
                                }
                            }

                            > .card {
                                display: inline-block;
                                vertical-align: top;
                                margin: 0 4px 0 4px;
                                cursor: pointer;
                                box-sizing: border-box;

                                &.selected {
                                    border: 2px solid colors.$green;
                                }
                            }
                        }
                    }
                }

                > .cardform {
                    overflow: auto;
                    padding: 30px 12px 0 12px;

                    > .formloader {
                        height: 36px;
                        margin-top: 20px;
                        text-align: center;
                        display: block;

                        > .center {
                            display: inline-block;
                            vertical-align: top;
                            line-height: 36px;

                            > .loader {
                                background-image: url("/assets/img/app/loading_green.svg");
                                display: inline-block;
                                vertical-align: top;
                                margin-right: 5px;
                            }
                        }
                    }

                    > iframe {
                        width: 100%;
                    }

                    > .stripecardform {
                        padding: 0 12px;
                    }
                }
            }

            &.shown {
                transform: translate3d(0, 0, 0);
                z-index: 410;

                > .inner {
                    opacity: 1;
                }
            }
        }

        > .method {
            height: 30px;
            line-height: 30px;
            border-bottom: 1px solid colors.$lightGrey;

            &.selected {
                color: colors.$green;
            }
        }

        .card {
            position: relative;
            background: #4f57b0;
            /* Old browsers */
            background: -moz-linear-gradient(left, #4f57b0 0%, #3c4383 100%);
            /* FF3.6-15 */
            background: -webkit-linear-gradient(left, #4f57b0 0%, #3c4383 100%);
            /* Chrome10-25,Safari5.1-6 */
            background: linear-gradient(to right, #4f57b0 0%, #3c4383 100%);
            /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
            filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#4f57b0', endColorstr='#3c4383', GradientType=1);
            /* IE6-9 */
            height: 43px;
            width: 64px;
            border-radius: 5px;
            overflow: hidden;

            > .brandicon {
                width: 22px;
                height: 14px;
                position: absolute;
                bottom: 5px;
                left: 3px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center center;

                &.visa {
                    height: 15px;
                    bottom: 3px;
                    background-image: url("/assets/img/brands/cc_visa_white.svg");
                }

                &.mastercard {
                    background-image: url("/assets/img/brands/cc_mastercard.svg");
                }

                &.unionpay {
                    background-image: url("/assets/img/brands/cc_unionpay.svg");
                }

                &.amex {
                    background-image: url("/assets/img/brands/cc_amex_white.svg");
                }

                &.jcb {
                    background-image: url("/assets/img/brands/cc_jcb.svg");
                }

                &.discover {
                    width: 48px;
                    background-image: url("/assets/img/brands/cc_discover.svg");
                }

                &.diners {
                    background-image: url("/assets/img/brands/cc_diners.svg");
                }
            }

            > .expiry {
                position: absolute;
                bottom: 5px;
                line-height: 12px;
                right: 5px;
                z-index: 411;
                font-size: 10px;
                color: #fff;
            }

            > .number {
                position: absolute;
                top: 4px;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 12px;
                line-height: 14px;
                font-weight: 700;
                color: #fff;
                letter-spacing: 1px;
                z-index: 411;

                > .dots {
                    display: inline-block;
                    vertical-align: top;
                    line-height: 14px;
                    font-size: 7.5px;
                    padding-right: 4px;
                }
            }
        }
    }
</style>
